import { useNavigation } from "@react-navigation/core"
import { StackNavigationProp } from "@react-navigation/stack"
import { ErrorMessage, Text, TouchButton, TouchButtonRow } from "pusatec-react-native"
import { useDebouncedState } from "pusatec-react-utility"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Modal, Pressable, TextInput, View } from "react-native"
import { SearchBar } from "react-native-elements"
import Base from "../../components/layout/Base"
import MainScrollPanel from "../../components/layout/MainScrollPanel"
import { useCreateItemGroupMutation, useSearchItemGroupsQuery } from "../../components/queries.generated"
import { colors, theme } from "../../theme"
import { AdminViewQuery } from "./queries.generated"
import { BiTrash } from "react-icons/bi"
import { AdminToolbar } from "./admin_toolbar"
import { DeleteItemGroupConfirmation } from "./DeleteItemGroupConfirmation"
import { HeaderBar } from "../../components/Headerbar"
import { MainStackParamList } from "../../MainNavigation"

type ItemGroup = NonNullable<AdminViewQuery["lockersystem"]>["itemGroups"][0]

const CreateNewItem = (props: {
    lockersystemId: string
    onSuccess: () => void
    onCanceled: () => void
}) => {
	const [itemGroupName, setItemGroupName] = useState("")
	const { t } = useTranslation()
	const [createItemGroup, createItemGroupM] = useCreateItemGroupMutation()

	return (
		<Modal transparent onRequestClose={props.onCanceled}>
			<View style={{
				flex: 1,
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "rgba(0,0,0,0.5)",
				padding: 20,
			}}>
				<View style={{
					width: "100%",
					maxWidth: 600,
					borderRadius: 10,
					padding: 20,
					backgroundColor: "white",
				}}>
					<Text text={t("New item group")} style={{ marginBottom: 8 }} />
					<TextInput
						placeholder={t("New item group")}
						onChangeText={text => {
							setItemGroupName(text)
						}}
						value={itemGroupName}
						style={{
							borderColor: "#c7c7c7",
							borderWidth: 1,
							borderRadius: 6,
							height: 44,
							paddingHorizontal: 12,
						}}
					/>

					{createItemGroupM.error ? <ErrorMessage extra={createItemGroupM.error} style={{ marginTop: 12 }} /> : null}

					<View style={{
						flexDirection: "row",
						justifyContent: "flex-end",
						marginTop: 16,
					}}>
						<TouchButtonRow>
							<TouchButton
								text={t("Cancel")}
								type="secondary"
								onPress={props.onCanceled}
							/>
							<TouchButton
								text={t("Create")}
								type="primary"
								loading={createItemGroupM.loading}
								disabled={!itemGroupName.trim() || createItemGroupM.loading}
								onPress={async () => {
									const result = await createItemGroup({
										variables: {
											input: {
												lockersystemId: props.lockersystemId,
												name: itemGroupName.trim()
											}
										}
									})

									if (result.errors?.length || !result.data?.createItemGroup) {
										return
									}

									props.onSuccess()
								}}
							/>
						</TouchButtonRow>
					</View>
				</View>
			</View>
		</Modal>
	)
}

interface Props {
    lockersystemId: string
    itemGroups: ItemGroup[]
}

export const ManageItemGroups = (props: Props) => {
	const [itemGroupSearch, itemGroupSearchd, setItemGroupSearch] = useDebouncedState("", 400)
	const [creatingItemGroup, setCreatingItemGroup] = useState<boolean>(false)
	const [showDeleteItemConfirmation, setShowDeleteItemConfirmation] = useState<boolean>(false)
	const [pendingDeleteItemGroupId, setPendingDeleteItemGroupId] = useState<string>("")
	const { t } = useTranslation()
	const navigation = useNavigation<StackNavigationProp<MainStackParamList, "AdminNavigation">>()

	const { data, refetch } = useSearchItemGroupsQuery({
		variables: {
			opt: {
				lockersystemId: props.lockersystemId,
				parentItemGroupId: null,
				searchWord: itemGroupSearchd
			}
		}
	})
	const pendingDeleteItemGroup = data?.itemGroups.itemGroups.find(itemGroup => itemGroup.id === pendingDeleteItemGroupId)

	return (
		<View style={[{ flex: 1 }]}>
			{showDeleteItemConfirmation && pendingDeleteItemGroup && <DeleteItemGroupConfirmation
				itemGroup={pendingDeleteItemGroup.id}
				name={pendingDeleteItemGroup.name}
				onExit={() => {
					setShowDeleteItemConfirmation(false)
					setPendingDeleteItemGroupId("")
				}}
			/>}
			<SearchBar
				lightTheme
				inputStyle={theme.Input.inputStyle}
				errorStyle={theme.Input.errorMessageStyle}
				labelStyle={theme.Input.labelStyle}
				containerStyle={theme.Input.containerStyle}
				inputContainerStyle={theme.Input.inputContainerStyle}
				value={itemGroupSearch}
				onChangeText={text => setItemGroupSearch(text)}
			/>

			{creatingItemGroup &&
                <CreateNewItem
                	lockersystemId={props.lockersystemId}
                	onCanceled={() => {
                		setCreatingItemGroup(false)
                	}}
                	onSuccess={() => {
                		setCreatingItemGroup(false)
                		refetch()
                	}}
                />}
			<MainScrollPanel
				style={{ borderWidth: 0, backgroundColor: undefined }}
				refreshing={false}
				onRefresh={() => { }}
			>
				<HeaderBar text={t("Groups")} />
				<View>
					{data?.itemGroups.itemGroups.map(itemGroup => {
						const limitValue = itemGroup.maxRentingsPerUser ?? t("Not set")
						const limitStatus = itemGroup.maxRentingsPerUserEnabled
							? t("Limit enabled")
							: t("Limit disabled")
						const textColor = itemGroup.maxRentingsPerUserEnabled ? colors.primary : colors.dark

						return (
							<Base
								key={itemGroup.id}
								selected={pendingDeleteItemGroupId === itemGroup.id}
								onPress={() => {
									navigation.push("ItemGroupLimit", { itemGroupId: itemGroup.id })
								}}
								style={{ padding: 16 }}
							>
								<View style={{ flexDirection: "row" }}>
									<View style={{ flex: 1, marginRight: 8 }}>
										<Text text={itemGroup.name} style={{ flexGrow: 1, fontWeight: "bold" }} />
										<Text
											text={`${t("Max rentings per user")}: ${limitValue}`}
											style={{ color: textColor, marginTop: 4 }}
										/>
										<Text text={limitStatus} style={{ color: textColor, marginTop: 2 }} />
										<Text
											text={itemGroup.borrowingRightsUserGroups.length > 0
												? t("Borrowing rights: {{count}} groups", { count: itemGroup.borrowingRightsUserGroups.length })
												: t("Borrowing rights: all users")}
											style={{ color: textColor, marginTop: 2 }}
										/>
									</View>
									<Pressable
										onPress={() => {
											setPendingDeleteItemGroupId(itemGroup.id)
											setShowDeleteItemConfirmation(true)
										}}
										style={{ justifyContent: "center" }}
									>
										<BiTrash size={30} style={{ color: "#f54058" }} />
									</Pressable>
								</View>
							</Base>
						)
					})}
				</View>
			</MainScrollPanel>
			<AdminToolbar>

				<View style={{ flexGrow: 1, justifyContent: "flex-end" }}>

					<TouchButton
						text={t("Create itemgroup")}
						onPress={() => {
							setCreatingItemGroup(true)
						}}
					/>
				</View>

			</AdminToolbar>
		</View>
	)
}
